import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  GlobalStyles,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InboxIcon from '@mui/icons-material/Inbox';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { getStats, KYCApiError } from '../services/kycApi';
import type { StatsResponse, UserSummary } from '../services/kycApi';

// ─────────────────────────────────────────────────────────────────────────────
// Paleta Amarello — navy oscuro #0f1221 con acentos naranja/dorado
// Basada en: playcity.com (theme-color meta, banners, CTAs)
// ─────────────────────────────────────────────────────────────────────────────
const C = {
  bg: '#0f1221',
  surface: 'rgba(255,255,255,0.04)',
  surfaceHover: 'rgba(255,255,255,0.07)',
  border: 'rgba(255,255,255,0.09)',
  text: '#f1f5f9',
  muted: 'rgba(255,255,255,0.42)',
  orange: '#f97316',
  gold: '#f59e0b',
  emerald: '#10b981',
  rose: '#f43f5e',
  blue: '#3b82f6',
};

// Gradientes de las KPI cards — identidad Amarello
const KPI_GRADIENTS = [
  // Procesos totales — navy/azul profundo (color de fondo del sitio Amarello)
  { from: '#0e1b4b', to: '#1e3a8a', accent: C.blue,    glow: '#3b82f655' },
  // Documentos revisados — dorado/casino
  { from: '#1c1507', to: '#3d2c08', accent: C.gold,    glow: '#f59e0b55' },
  // Tasa de aprobación — verde (aprobado)
  { from: '#052e16', to: '#065f46', accent: C.emerald, glow: '#10b98155' },
  // Docs entregados — naranja Amarello (color CTA principal)
  { from: '#1f0d04', to: '#431407', accent: C.orange,  glow: '#f9731655' },
];

// Barras de progreso por estado de decisión KYC
const STATUS_BAR: Record<string, { from: string; to: string; glow: string }> = {
  APROBADO:                { from: '#059669', to: '#34d399', glow: '#10b98155' },
  RECHAZADO:               { from: '#be123c', to: '#f43f5e', glow: '#f43f5e44' },
  'APROBADO CONDICIONADO': { from: C.gold,    to: '#fbbf24', glow: '#f59e0b44' },
};
const DEFAULT_BAR = { from: '#334155', to: '#64748b', glow: '#64748b33' };

const STATUS_LABELS: Record<string, string> = {
  APROBADO:                'Aprobado',
  RECHAZADO:               'Rechazado',
  'APROBADO CONDICIONADO': 'Aprobado condicional',
  DESCONOCIDO:             'Desconocido',
  UNKNOWN:                 'Desconocido',
};

const DOC_TYPE_META: Record<string, { icon: string; color: string }> = {
  INE:              { icon: '🪪', color: C.blue    },
  PASSPORT:         { icon: '🛂', color: C.gold    },
  PROOF_OF_ADDRESS: { icon: '🏠', color: C.orange  },
  BANK_STATEMENT:   { icon: '🏦', color: C.emerald },
};
const DEFAULT_DOC = { icon: '📄', color: '#94a3b8' };

function formatDate(iso: string | null | undefined): string {
  if (!iso) return 'N/A';
  return new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
}

// ─────────────────────────────────────────────────────────────────────────────
// KPI Card
// ─────────────────────────────────────────────────────────────────────────────
interface KpiCardProps {
  label: string;
  value: number;
  suffix?: string;
  icon: React.ReactNode;
  gradientIndex: number;
}

function KpiCard({ label, value, suffix = '', icon, gradientIndex }: KpiCardProps) {
  const g = KPI_GRADIENTS[gradientIndex];
  return (
    <Box
      sx={{
        flex: '1 1 160px',
        minWidth: 140,
        borderRadius: 2.5,
        p: 2.5,
        background: `linear-gradient(135deg, ${g.from}, ${g.to})`,
        border: `1px solid ${g.accent}28`,
        boxShadow: `0 0 24px ${g.glow}, inset 0 1px 0 rgba(255,255,255,0.08)`,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -24,
          right: -24,
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${g.accent}22, transparent 70%)`,
        },
      }}
    >
      <Box sx={{ color: g.accent, display: 'flex', alignItems: 'center' }}>
        {icon}
      </Box>
      <Typography
        sx={{
          fontSize: '2.25rem',
          fontWeight: 800,
          lineHeight: 1,
          color: '#fff',
          letterSpacing: '-0.03em',
          animation: 'kyc-fade-up 0.5s ease both',
        }}
      >
        {value.toLocaleString('es-MX')}{suffix}
      </Typography>
      <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.55)', fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
        {label}
      </Typography>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Fila de breakdown con barra de progreso
// ─────────────────────────────────────────────────────────────────────────────
interface BreakdownRowProps {
  label: string;
  count: number;
  total: number;
  barColors: { from: string; to: string; glow: string };
  prefix?: string;
  prefixColor?: string;
  onClick?: () => void;
}

function BreakdownRow({ label, count, total, barColors, prefix, prefixColor, onClick }: BreakdownRowProps) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  const clickable = typeof onClick === 'function';
  return (
    <Box
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } } : undefined}
      sx={{
        mb: 2,
        p: 1.5,
        borderRadius: 2,
        border: `1px solid ${C.border}`,
        background: C.surface,
        cursor: clickable ? 'pointer' : 'default',
        transition: 'background 0.2s, border-color 0.2s, box-shadow 0.2s',
        '&:hover': clickable
          ? { background: C.surfaceHover, borderColor: barColors.from, boxShadow: `0 0 14px ${barColors.glow}` }
          : { background: C.surfaceHover },
        '&:focus-visible': clickable
          ? { outline: 'none', borderColor: barColors.from, boxShadow: `0 0 14px ${barColors.glow}` }
          : undefined,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {prefix && (
            <Typography sx={{ fontSize: '1rem', lineHeight: 1 }}>{prefix}</Typography>
          )}
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: prefixColor ?? C.text }}>
            {label}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#fff' }}>
            {count.toLocaleString('es-MX')}
          </Typography>
          <Typography
            sx={{
              fontSize: '0.72rem',
              fontWeight: 600,
              color: barColors.from,
              bgcolor: `${barColors.from}18`,
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              border: `1px solid ${barColors.from}30`,
            }}
          >
            {pct}%
          </Typography>
        </Box>
      </Box>
      <Box sx={{ height: 5, borderRadius: 99, bgcolor: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
        <Box
          sx={{
            height: '100%',
            width: `${pct}%`,
            borderRadius: 99,
            background: `linear-gradient(90deg, ${barColors.from}, ${barColors.to})`,
            boxShadow: `0 0 8px ${barColors.glow}`,
            transition: 'width 1.2s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        />
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sección de breakdown con header y separador
// ─────────────────────────────────────────────────────────────────────────────
function BreakdownSection({ title, accent, children }: { title: string; accent: string; children: React.ReactNode }) {
  return (
    <Box
      sx={{
        borderRadius: 3,
        border: `1px solid ${C.border}`,
        background: 'rgba(255,255,255,0.025)',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          borderBottom: `1px solid ${C.border}`,
          background: 'rgba(255,255,255,0.02)',
        }}
      >
        <Box sx={{ width: 3, height: 16, borderRadius: 99, bgcolor: accent, boxShadow: `0 0 6px ${accent}` }} />
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: C.muted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Box>
  );
}

function EmptyState() {
  return (
    <Box sx={{ py: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
      <InboxIcon sx={{ fontSize: 36, color: C.muted, opacity: 0.5 }} />
      <Typography sx={{ fontSize: '0.85rem', color: C.muted }}>
        Sin registros aún — los datos aparecerán una vez que inicien sesiones KYC
      </Typography>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Modal con detalle de usuarios filtrados (drill-down del breakdown)
// ─────────────────────────────────────────────────────────────────────────────
interface UserDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  users: UserSummary[];
  accent: string;
}

function UserDetailsDialog({ open, onClose, title, users, accent }: UserDetailsDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: C.bg,
          color: C.text,
          border: `1px solid ${accent}55`,
          boxShadow: `0 0 28px ${accent}33`,
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pb: 1.5,
          borderBottom: `1px solid ${C.border}`,
        }}
      >
        <Box sx={{ width: 4, height: 18, borderRadius: 99, bgcolor: accent, boxShadow: `0 0 6px ${accent}` }} />
        <Typography sx={{ flex: 1, fontSize: '1rem', fontWeight: 700, color: C.text }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: accent }}>
          {users.length} {users.length === 1 ? 'usuario' : 'usuarios'}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: C.muted, ml: 1 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {users.length === 0 ? (
          <Box sx={{ py: 6 }}>
            <EmptyState />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: '60vh' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ background: C.bg, color: C.muted, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: `1px solid ${C.border}` }}>
                    User ID
                  </TableCell>
                  <TableCell align="center" sx={{ background: C.bg, color: C.muted, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: `1px solid ${C.border}` }}>
                    Intentos
                  </TableCell>
                  <TableCell sx={{ background: C.bg, color: C.muted, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: `1px solid ${C.border}` }}>
                    Último intento
                  </TableCell>
                  <TableCell sx={{ background: C.bg, color: C.muted, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: `1px solid ${C.border}` }}>
                    Última actualización
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.user_id} hover sx={{ '&:hover': { background: C.surfaceHover } }}>
                    <TableCell sx={{ color: C.text, fontFamily: 'monospace', fontSize: '0.78rem', borderBottom: `1px solid ${C.border}` }}>
                      {u.user_id}
                    </TableCell>
                    <TableCell align="center" sx={{ color: C.text, fontWeight: 700, borderBottom: `1px solid ${C.border}` }}>
                      {u.attempts_count}
                    </TableCell>
                    <TableCell sx={{ color: C.text, fontSize: '0.82rem', borderBottom: `1px solid ${C.border}` }}>
                      {u.last_attempt_status ?? '—'}
                    </TableCell>
                    <TableCell sx={{ color: C.muted, fontSize: '0.78rem', borderBottom: `1px solid ${C.border}` }}>
                      {u.last_updated ? formatDate(u.last_updated) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────
interface StatsPageProps {
  onBack: () => void;
}

interface DialogState {
  open: boolean;
  title: string;
  users: UserSummary[];
  accent: string;
}

export function StatsPage({ onBack }: StatsPageProps) {
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogState>({ open: false, title: '', users: [], accent: C.orange });

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getStats();
      setStats(data);
    } catch (err) {
      if (err instanceof KYCApiError) {
        setError(`Error ${err.status}: ${err.message}`);
      } else {
        setError('No se pudo conectar con el servidor de estadísticas.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const openStatusDetail = useCallback((statusBucket: string, accent: string) => {
    if (!stats) return;
    const filtered = stats.users.filter((u) => u.status_bucket === statusBucket);
    const label = STATUS_LABELS[statusBucket] ?? statusBucket;
    setDialog({ open: true, title: `Usuarios en estado: ${label}`, users: filtered, accent });
  }, [stats]);

  const openAttemptStatusDetail = useCallback((attemptStatus: string, accent: string) => {
    if (!stats) return;
    const filtered = stats.users.filter((u) => u.last_attempt_status === attemptStatus);
    setDialog({ open: true, title: `Usuarios con último intento: ${attemptStatus}`, users: filtered, accent });
  }, [stats]);

  const closeDialog = useCallback(() => setDialog((prev) => ({ ...prev, open: false })), []);

  // Tasa de aprobación = users con bucket APROBADO / total de usuarios (no de intentos).
  // Coherente con el modelo P2 + A2 — claims_by_status cuenta usuarios, no documentos.
  const approvalRate = stats && stats.total_processes > 0
    ? Math.round(((stats.claims_by_status['APROBADO'] ?? 0) / stats.total_processes) * 100)
    : 0;

  const totalByType = stats
    ? Object.values(stats.claims_by_type).reduce((a, b) => a + b, 0)
    : 0;

  const hasStatusData = stats && Object.keys(stats.claims_by_status).length > 0;
  const hasTypeData = stats && Object.keys(stats.claims_by_type).length > 0;

  return (
    <>
      <GlobalStyles styles={{
        '@keyframes kyc-fade-up': {
          from: { opacity: 0, transform: 'translateY(6px)' },
          to:   { opacity: 1, transform: 'translateY(0)' },
        },
        '@keyframes kyc-spin': {
          from: { transform: 'rotate(0deg)' },
          to:   { transform: 'rotate(360deg)' },
        },
        '@keyframes kyc-shimmer': {
          '0%':   { backgroundPosition: '-200% center' },
          '100%': { backgroundPosition: '200% center' },
        },
      }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: C.bg }}>

        {/* ── Header ── */}
        <Box
          sx={{
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            background: `rgba(15,18,33,0.9)`,
            borderBottom: `1px solid ${C.border}`,
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
        >
          {/* Línea naranja decorativa en la parte superior — estilo Amarello */}
          <Box
            sx={{
              height: 2,
              background: `linear-gradient(90deg, transparent, ${C.orange}, ${C.gold}, ${C.orange}, transparent)`,
              backgroundSize: '200% auto',
              animation: 'kyc-shimmer 3s linear infinite',
            }}
          />
          <Toolbar sx={{ gap: 2, minHeight: '56px !important' }}>
            <Button
              startIcon={<ArrowBackIcon sx={{ fontSize: '16px !important' }} />}
              onClick={onBack}
              size="small"
              sx={{
                color: C.muted,
                borderColor: C.border,
                border: '1px solid',
                borderRadius: 1.5,
                fontSize: '0.75rem',
                textTransform: 'none',
                px: 1.5,
                '&:hover': { color: C.text, borderColor: 'rgba(255,255,255,0.25)', bgcolor: C.surface },
              }}
            >
              Volver
            </Button>

            <Box sx={{ flex: 1 }} />

            {/* Última actualización */}
            {stats && (
              <Typography sx={{ fontSize: '0.7rem', color: C.muted, display: { xs: 'none', sm: 'block' } }}>
                {formatDate(stats.last_updated as unknown as string)}
              </Typography>
            )}

            {/* Botón Actualizar — estilo Amarello naranja */}
            <Button
              onClick={fetchStats}
              disabled={loading}
              size="small"
              startIcon={
                loading
                  ? <CircularProgress size={12} sx={{ color: C.orange }} />
                  : <RefreshIcon sx={{ fontSize: '15px !important', color: loading ? undefined : C.orange }} />
              }
              sx={{
                color: C.orange,
                border: `1px solid ${C.orange}40`,
                borderRadius: 1.5,
                fontSize: '0.75rem',
                textTransform: 'none',
                px: 1.5,
                background: `${C.orange}08`,
                '&:hover': { background: `${C.orange}18`, borderColor: `${C.orange}80` },
                '&:disabled': { color: C.muted, borderColor: C.border },
              }}
            >
              Actualizar
            </Button>
          </Toolbar>
        </Box>

        {/* ── Contenido ── */}
        <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 940, mx: 'auto', width: '100%' }}>

          {/* Cargando */}
          {loading && !stats && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 12 }}>
              <CircularProgress sx={{ color: C.orange }} />
            </Box>
          )}

          {/* Error */}
          {error && !stats && (
            <Box
              sx={{
                mt: 8,
                textAlign: 'center',
                p: 4,
                borderRadius: 3,
                border: `1px solid ${C.rose}30`,
                background: `${C.rose}08`,
              }}
            >
              <Typography sx={{ color: C.rose, fontSize: '0.9rem', mb: 2 }}>{error}</Typography>
              <Button
                onClick={fetchStats}
                size="small"
                sx={{
                  color: C.rose,
                  border: `1px solid ${C.rose}50`,
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontSize: '0.8rem',
                  '&:hover': { background: `${C.rose}12` },
                }}
              >
                Reintentar
              </Button>
            </Box>
          )}

          {/* Contenido principal */}
          {stats && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, animation: 'kyc-fade-up 0.4s ease both' }}>

              {/* ── KPI Cards ── */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <KpiCard
                  label="Procesos totales"
                  value={stats.total_processes}
                  icon={<AssignmentTurnedInIcon sx={{ fontSize: 20 }} />}
                  gradientIndex={0}
                />
                <KpiCard
                  label="Documentos revisados"
                  value={stats.total_claims}
                  icon={<DescriptionOutlinedIcon sx={{ fontSize: 20 }} />}
                  gradientIndex={1}
                />
                <KpiCard
                  label="Tasa de aprobación"
                  value={approvalRate}
                  suffix="%"
                  icon={<ShowChartIcon sx={{ fontSize: 20 }} />}
                  gradientIndex={2}
                />
                <KpiCard
                  label="Docs entregados a Cliente"
                  value={stats.odp_deliveries}
                  icon={<CloudUploadIcon sx={{ fontSize: 20 }} />}
                  gradientIndex={3}
                />
              </Box>

              {/* ── Breakdown por estado ── (cada fila clickeable → drill-down) */}
              <BreakdownSection title="Usuarios por estado" accent={C.orange}>
                {hasStatusData ? (
                  Object.entries(stats.claims_by_status)
                    .sort(([, a], [, b]) => b - a)
                    .map(([status, count]) => {
                      const bar = STATUS_BAR[status] ?? DEFAULT_BAR;
                      return (
                        <BreakdownRow
                          key={status}
                          label={STATUS_LABELS[status] ?? status}
                          count={count}
                          total={stats.total_processes}
                          barColors={bar}
                          prefixColor={bar.to}
                          onClick={() => openStatusDetail(status, bar.from)}
                        />
                      );
                    })
                ) : (
                  <EmptyState />
                )}
              </BreakdownSection>

              {/* ── Breakdown por tipo de intento (último attempt_status del user) ── */}
              <BreakdownSection title="Por último intento de prueba de vida" accent={C.gold}>
                {hasTypeData ? (
                  Object.entries(stats.claims_by_type)
                    .sort(([, a], [, b]) => b - a)
                    .map(([type, count]) => {
                      const meta = DOC_TYPE_META[type] ?? DEFAULT_DOC;
                      return (
                        <BreakdownRow
                          key={type}
                          label={type.replace(/_/g, ' ')}
                          count={count}
                          total={totalByType}
                          barColors={{ from: meta.color, to: meta.color, glow: `${meta.color}44` }}
                          prefix={meta.icon}
                          prefixColor={meta.color}
                          onClick={() => openAttemptStatusDetail(type, meta.color)}
                        />
                      );
                    })
                ) : (
                  <EmptyState />
                )}
              </BreakdownSection>

              {/* Footer */}
              <Typography sx={{ textAlign: 'center', fontSize: '0.7rem', color: C.muted, pb: 2 }}>
                Última sincronización: {formatDate(stats.last_updated as unknown as string)}
              </Typography>

            </Box>
          )}
        </Box>
      </Box>

      <UserDetailsDialog
        open={dialog.open}
        onClose={closeDialog}
        title={dialog.title}
        users={dialog.users}
        accent={dialog.accent}
      />
    </>
  );
}
